import { apiClient } from "./client";
import type { Assignment, Planning, PlanningView, RealizedStatus } from "./types";

export async function getPlanning(
  organizationId: string,
  year: number,
  month: number,
): Promise<PlanningView> {
  const { data } = await apiClient.get<PlanningView>(
    `/api/v1/plannings/${organizationId}/${year}/${month}`,
  );
  return data;
}

export type CreateAssignmentPayload = {
  date: string; // YYYY-MM-DD
  slot_template_id: string;
  practitioner_id: string;
  exercise_mode_id: string;
  /** Functional role held during the shift (Med 1, ARM 2…). */
  functional_role_id?: string | null;
  comment?: string | null;
};

export async function createAssignment(
  organizationId: string,
  year: number,
  month: number,
  payload: CreateAssignmentPayload,
): Promise<Assignment> {
  const { data } = await apiClient.post<Assignment>(
    `/api/v1/plannings/${organizationId}/${year}/${month}/assignments`,
    payload,
  );
  return data;
}

/**
 * In-place correction of a shift. Omit a field to leave it untouched — sending
 * `null` on a nullable field clears it, which is a different intent.
 */
export type UpdateAssignmentPayload = {
  date?: string;
  slot_template_id?: string;
  practitioner_id?: string;
  exercise_mode_id?: string;
  functional_role_id?: string | null;
  comment?: string | null;
};

export async function updateAssignment(
  organizationId: string,
  year: number,
  month: number,
  assignmentId: string,
  payload: UpdateAssignmentPayload,
): Promise<Assignment> {
  const { data } = await apiClient.patch<Assignment>(
    `/api/v1/plannings/${organizationId}/${year}/${month}/assignments/${assignmentId}`,
    payload,
  );
  return data;
}

export type RealizePayload = {
  realized_status: RealizedStatus;
  actual_start_at?: string | null;
  actual_end_at?: string | null;
  realized_minutes?: number | null;
  manager_note?: string | null;
};

export async function realizeAssignment(
  organizationId: string,
  year: number,
  month: number,
  assignmentId: string,
  payload: RealizePayload,
): Promise<Assignment> {
  const { data } = await apiClient.post<Assignment>(
    `/api/v1/plannings/${organizationId}/${year}/${month}/assignments/${assignmentId}/realize`,
    payload,
  );
  return data;
}

export type BulkRealizePayload = {
  date_from: string;
  date_to: string;
  practitioner_id?: string | null;
  only_status?: RealizedStatus;
  target_status?: RealizedStatus;
};

export async function bulkRealize(
  organizationId: string,
  year: number,
  month: number,
  payload: BulkRealizePayload,
): Promise<{ updated: number; skipped_locked: number }> {
  const { data } = await apiClient.post<{ updated: number; skipped_locked: number }>(
    `/api/v1/plannings/${organizationId}/${year}/${month}/realize-bulk`,
    {
      only_status: "PLANNED",
      target_status: "DONE",
      ...payload,
    },
  );
  return data;
}

export async function deleteAssignment(
  organizationId: string,
  year: number,
  month: number,
  assignmentId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/plannings/${organizationId}/${year}/${month}/assignments/${assignmentId}`,
  );
}

export async function publishPlanning(
  organizationId: string,
  year: number,
  month: number,
): Promise<Planning> {
  const { data } = await apiClient.post<Planning>(
    `/api/v1/plannings/${organizationId}/${year}/${month}/publish`,
  );
  return data;
}

// ----------------------------------------------------------------------------
// CP-SAT auto-generation
// ----------------------------------------------------------------------------
export type GenerateReport = {
  status: "OPTIMAL" | "FEASIBLE" | "INFEASIBLE" | "MODEL_INVALID" | "UNKNOWN";
  elapsed_ms: number;
  days: number;
  slots: number;
  practitioners: number;
  total_slots_to_fill: number;
  slots_filled: number;
  carences: number;
  wishes_satisfied: number;
  wishes_total: number;
  avoids_violated: number;
  avoids_total: number;
  unavailables_respected: number;
  objective_value: number | null;
  assignments_created: number;
  coverage_rate: number;
};

export type SolverPresetName =
  | "BALANCED"
  | "MAX_COVERAGE"
  | "MAX_EQUITY"
  | "RESPECT_WISHES";

export type SolverPreset = {
  name: SolverPresetName;
  carence: number;
  avoid: number;
  wish: number;
  equity: number;
};

export type GenerateOptions = {
  overwrite_auto?: boolean;
  time_limit_seconds?: number;
  seed?: number;
  preset?: SolverPresetName;
  weight_carence?: number;
  weight_avoid?: number;
  weight_wish?: number;
  weight_equity?: number;
};

export async function generatePlanning(
  organizationId: string,
  year: number,
  month: number,
  options: GenerateOptions = {},
): Promise<GenerateReport> {
  const { data } = await apiClient.post<GenerateReport>(
    `/api/v1/plannings/${organizationId}/${year}/${month}/generate`,
    {
      overwrite_auto: options.overwrite_auto ?? true,
      time_limit_seconds: options.time_limit_seconds ?? 30,
      seed: options.seed ?? 42,
      preset: options.preset ?? null,
      weight_carence: options.weight_carence ?? null,
      weight_avoid: options.weight_avoid ?? null,
      weight_wish: options.weight_wish ?? null,
      weight_equity: options.weight_equity ?? null,
    },
    { timeout: 120_000 },
  );
  return data;
}

export async function listSolverPresets(): Promise<SolverPreset[]> {
  const { data } = await apiClient.get<SolverPreset[]>(
    "/api/v1/plannings/generate/presets",
  );
  return data;
}


export type GapReason = {
  practitioner_id: string;
  last_name: string;
  first_initial: string | null;
  reason: "busy_same_date" | "unavailable_pref" | "on_time_off" | "missing_skill";
};

export type GapCell = {
  date: string;
  slot_template_id: string;
  slot_label: string;
  candidates_total: number;
  free_candidates: number;
  blocked: GapReason[];
};

export type GapExplainResponse = {
  organization_id: string;
  year: number;
  month: number;
  cells: GapCell[];
};

export async function explainGaps(
  organizationId: string,
  year: number,
  month: number,
): Promise<GapExplainResponse> {
  const { data } = await apiClient.get<GapExplainResponse>(
    `/api/v1/plannings/${organizationId}/${year}/${month}/explain-gaps`,
  );
  return data;
}


export type BulkPublishItem = {
  organization_id: string;
  year: number;
  month: number;
};

export type BulkPublishError = {
  organization_id: string;
  year: number;
  month: number;
  detail: string;
};

export type BulkPublishResult = {
  requested: number;
  published: number;
  skipped_locked: number;
  skipped_already_published: number;
  errors: BulkPublishError[];
};

export async function bulkPublishPlannings(
  items: BulkPublishItem[],
): Promise<BulkPublishResult> {
  const { data } = await apiClient.post<BulkPublishResult>(
    "/api/v1/plannings/bulk-publish",
    { items },
  );
  return data;
}
